import { useEffect, useRef, useState } from 'react';
import { messageElementId } from './messageElementId.ts';
import type { PlacedFinding } from './placeFindings.ts';
import type { Transcript } from './useTranscript.ts';
import type { TranscriptScroll } from './useTranscriptScroll.ts';

/** Where the arrows of the findings bar are taking the reader. */
interface Jump {
  key: string;
  /** The jump a transcript opens with: when it finds nothing to land on, it lands at the end. */
  opening: boolean;
}

export interface FindingNavigationInput {
  /** Which transcript is open, person and conversation; null when none is. */
  openedKey: string | null;
  findings: PlacedFinding[];
  notesByMessage: Map<string, PlacedFinding[]>;
  transcript: Transcript;
  scroll: TranscriptScroll;
  belowMobile: boolean;
}

export interface FindingNavigation {
  /** The finding the bar is on. */
  currentFinding: string | null;
  selectFinding: (key: string) => void;
}

/**
 * The findings bar and the transcript, kept on the same finding: the arrows
 * jump to one, reading older pages until it can be placed, and scrolling past
 * one makes it the current one.
 */
export function useFindingNavigation({
  openedKey,
  findings,
  notesByMessage,
  transcript,
  scroll,
  belowMobile,
}: FindingNavigationInput): FindingNavigation {
  const { loading, loadingMore, hasMore } = transcript;
  const { scroller, scrollToEnd, readOlder } = scroll;

  /* The finding picked with the arrows, in the transcript it was picked in. */
  const [pickedFinding, setPickedFinding] = useState<{
    transcript: string;
    key: string;
  } | null>(null);
  const jump = useRef<Jump | null>(null);
  /* Off while the page itself moves the transcript; the reader's own scrolling turns it on. */
  const following = useRef(false);

  /* Until one is picked, the bar is on the first: where the transcript opened. */
  const currentFinding =
    pickedFinding && pickedFinding.transcript === openedKey
      ? pickedFinding.key
      : (findings[0]?.key ?? null);

  /* Which transcript was last opened, so crossing the breakpoint leaves it alone. */
  const opened = useRef<string | null>(null);

  /*
   * A transcript opens at its first open finding, which is what there is to
   * look at; without one, at its end, where the conversation was left. Only a
   * transcript: on a phone the box is the page, and the list must stay put.
   */
  useEffect(() => {
    if (!openedKey) {
      opened.current = null;
      return;
    }
    if (loading || opened.current === openedKey) return;
    opened.current = openedKey;

    const first = findings[0];
    following.current = false;
    jump.current = first ? { key: first.key, opening: true } : null;
    if (!first) scrollToEnd();
  }, [openedKey, loading, findings, scrollToEnd]);

  /*
   * A finding whose conversation is not read back far enough yet waits for
   * older pages, one at a time, until it can be placed or never can.
   */
  useEffect(() => {
    const pending = jump.current;
    if (!pending || loading) return;

    const target = findings.find((placed) => placed.key === pending.key);
    if (!target) {
      jump.current = null;
      return;
    }

    if (target.messageId !== null) {
      document.getElementById(messageElementId(target.messageId))?.scrollIntoView({
        block: 'center',
        behavior: pending.opening ? 'auto' : 'smooth',
      });
      jump.current = null;
      return;
    }

    if (!target.read) {
      if (hasMore && !loadingMore) readOlder();
      return;
    }

    /* Under no message: the bar shows it, and there is nowhere to go. */
    if (pending.opening) scrollToEnd();
    jump.current = null;
  }, [currentFinding, loading, findings, hasMore, loadingMore, readOlder, scrollToEnd]);

  /*
   * The bar follows the reader: a message with a finding that crosses the
   * middle of the transcript becomes the current one, so the arrows go on from
   * what is on screen rather than from the last one jumped to.
   */
  useEffect(() => {
    if (!openedKey || notesByMessage.size === 0) return;

    const keys = new Map<Element, string>();
    for (const [messageId, notes] of notesByMessage) {
      const element = document.getElementById(messageElementId(messageId));
      if (element) keys.set(element, notes[0].key);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!following.current) return;
        const crossing = entries.filter((entry) => entry.isIntersecting).at(-1);
        const key = crossing && keys.get(crossing.target);
        if (key) setPickedFinding({ transcript: openedKey, key });
      },
      {
        root: belowMobile ? null : scroller.current,
        rootMargin: '-40% 0px -40% 0px',
      },
    );
    for (const element of keys.keys()) observer.observe(element);
    return () => observer.disconnect();
  }, [openedKey, notesByMessage, belowMobile, scroller]);

  useEffect(() => {
    const follow = () => {
      following.current = true;
    };
    const events = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;
    for (const event of events) window.addEventListener(event, follow, { passive: true });
    return () => {
      for (const event of events) window.removeEventListener(event, follow);
    };
  }, []);

  function selectFinding(key: string) {
    if (!openedKey) return;
    following.current = false;
    jump.current = { key, opening: false };
    setPickedFinding({ transcript: openedKey, key });
  }

  return { currentFinding, selectFinding };
}
